import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../../lib/api";
import { CheckCircle2, AlertCircle, FileText, Eye, ExternalLink, ShieldCheck, X } from "lucide-react";

const MEDICAL_EDITORIAL_TYPES = new Set(["article", "condition", "symptom", "comparison"]);
const isMedicalEditorial = (pageType) => MEDICAL_EDITORIAL_TYPES.has(pageType);

/**
 * Reviewer inbox — surfaces every non-published draft with the metadata a
 * medical reviewer needs to triage:
 *   • Word count, verified citation count, reviewer-flag count
 *   • Assigned reviewer + evidence-rating status
 *   • Direct links to a full draft preview (rendered exactly as the public
 *     article will look), and to the AI-generated reviewer_flags list.
 *
 * Nothing on this page reveals draft URLs to the public. Everything routes
 * through the authenticated `/admin/content/{type}/{slug}` endpoint.
 */
export function ReviewerInbox() {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);

  useEffect(() => {
    api.get("/admin/content")
      .then(({ data }) => {
        // Only non-published rows — reviewer's queue
        const drafts = data.filter((r) => {
          const s = r.status || (r.public_visibility ? "published" : "draft");
          return s !== "published" && s !== "archived";
        });
        setRows(drafts);
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load review queue"));
  }, []);

  const aiOnly = useMemo(() => rows.filter((r) => r.ai_generated), [rows]);
  const humanOnly = useMemo(() => rows.filter((r) => !r.ai_generated), [rows]);

  // Non-medical editorial pages (provider bios, legal pages, therapies, faq
  // scaffold, location scaffold) continue to use the legacy one-click approve.
  const approveLegacy = async (r) => {
    if (!window.confirm(`Approve & publish "${r.title}"?\n\nOnly do this after verifying every reviewer flag and confirming citations.`)) return;
    try {
      await api.post(`/admin/content/${r.page_type}/${r.slug}/approve`);
      toast.success(`Published: ${r.title}`);
      setRows((cur) => cur.filter((x) => !(x.page_type === r.page_type && x.slug === r.slug)));
      setSelected(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approval failed");
    }
  };

  // Medical editorial pages (article, condition, symptom, comparison) trigger
  // the Phase 4 confirmation modal — publisher must tick "I confirm Dr.
  // Yessenia Quirola has reviewed and approved this medical content" and
  // clear all validator blockers before /publish-medical accepts.
  const openConfirmModal = async (r) => {
    try {
      const { data } = await api.get(
        `/admin/content/${r.page_type}/${r.slug}/validate-publish`
      );
      setConfirmModal({ row: r, report: data });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to run pre-flight validator");
    }
  };

  const requestApproval = (r) => {
    if (isMedicalEditorial(r.page_type)) {
      openConfirmModal(r);
    } else {
      approveLegacy(r);
    }
  };

  const submitMedicalPublish = async (row, checked) => {
    if (!checked) {
      toast.error("You must confirm Dr. Quirola's review before publishing.");
      return;
    }
    try {
      const { data } = await api.post(
        `/admin/content/${row.page_type}/${row.slug}/publish-medical`,
        { medical_review_confirmed: true }
      );
      toast.success(`Published: ${row.title} — Dr. Quirola credited`);
      setRows((cur) => cur.filter((x) => !(x.page_type === row.page_type && x.slug === row.slug)));
      setSelected(null);
      setConfirmModal(null);
      return data;
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.blockers) {
        toast.error(`Blocked: ${detail.blockers.join(" · ")}`);
      } else {
        toast.error(detail || "Publish failed");
      }
    }
  };

  return (
    <div data-testid="reviewer-inbox" className="grid lg:grid-cols-[320px_1fr] gap-6">
      {/* Left column — draft queue */}
      <aside>
        <div className="text-xs uppercase tracking-widest text-stone-500 mb-3">
          Review queue ({rows.length})
        </div>
        <div className="space-y-2">
          {aiOnly.length ? (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-indigo-700 font-semibold mb-1 flex items-center gap-1.5">
                <AlertCircle size={12} /> AI drafts ({aiOnly.length})
              </div>
              {aiOnly.map((r) => (
                <QueueItem key={r.page_type + r.slug} row={r} selected={selected} onSelect={setSelected} />
              ))}
            </div>
          ) : null}
          {humanOnly.length ? (
            <div className="pt-3">
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-1">
                Human drafts ({humanOnly.length})
              </div>
              {humanOnly.map((r) => (
                <QueueItem key={r.page_type + r.slug} row={r} selected={selected} onSelect={setSelected} />
              ))}
            </div>
          ) : null}
          {rows.length === 0 ? (
            <div className="vv-card p-4 text-sm text-stone-500 text-center">
              <CheckCircle2 className="mx-auto mb-2 text-green-600" />
              Inbox zero. No drafts awaiting review.
            </div>
          ) : null}
        </div>
      </aside>

      {/* Right column — reviewer panel */}
      <section>
        {selected ? <ReviewerPanel row={selected} onApprove={requestApproval} /> : (
          <div className="vv-card p-10 text-center text-stone-500">
            <FileText size={32} className="mx-auto mb-3 opacity-50" />
            <p>Select a draft from the left to review it.</p>
          </div>
        )}
      </section>

      {confirmModal ? (
        <MedicalPublishModal
          row={confirmModal.row}
          report={confirmModal.report}
          onCancel={() => setConfirmModal(null)}
          onConfirm={(checked) => submitMedicalPublish(confirmModal.row, checked)}
        />
      ) : null}
    </div>
  );
}

function QueueItem({ row, selected, onSelect }) {
  const isSelected = selected && selected.page_type === row.page_type && selected.slug === row.slug;
  const status = row.status || (row.public_visibility ? "published" : "draft");
  const flagCount = (row.reviewer_flags || []).length;
  const refCount = (row.references || []).length;
  return (
    <button
      data-testid={`inbox-item-${row.page_type}-${row.slug}`}
      onClick={() => onSelect(row)}
      className={`w-full text-left vv-card p-3 hover:border-[color:var(--vv-gold)] transition-colors ${isSelected ? "border-[color:var(--vv-gold-deep)] ring-1 ring-[color:var(--vv-gold-deep)]" : ""}`}
    >
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{row.title}</div>
          <div className="text-[11px] text-stone-500 truncate">{row.page_type} · {row.slug}</div>
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            {row.ai_generated ? (
              <span className="text-[10px] bg-indigo-50 text-indigo-800 border border-indigo-200 rounded-full px-1.5 py-0.5">AI draft</span>
            ) : null}
            <span className="text-[10px] bg-stone-100 text-stone-700 border border-stone-200 rounded-full px-1.5 py-0.5">{status.replace("_", " ")}</span>
            {flagCount ? <span className="text-[10px] bg-amber-50 text-amber-900 border border-amber-200 rounded-full px-1.5 py-0.5">{flagCount} flags</span> : null}
            <span className="text-[10px] text-stone-500">{refCount} refs</span>
          </div>
        </div>
      </div>
    </button>
  );
}

function ReviewerPanel({ row, onApprove }) {
  const status = row.status || (row.public_visibility ? "published" : "draft");
  const wc = row.extra?.word_count || 0;
  const previewUrl = `/admin/preview/${row.page_type}/${row.slug}`;

  return (
    <div className="space-y-4" data-testid="reviewer-panel">
      <div className="vv-card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="text-xs uppercase tracking-widest text-stone-500">{row.page_type}</div>
            <h2 className="font-display text-2xl mt-1" data-testid="reviewer-panel-title">{row.title}</h2>
            <div className="text-xs text-stone-500 mt-1">/{row.page_type === "article" ? "articles" : row.page_type}/{row.slug}</div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link
              to={previewUrl}
              target="_blank"
              data-testid="reviewer-open-preview"
              className="inline-flex items-center gap-1.5 text-sm bg-white border border-stone-200 hover:border-stone-400 rounded-full px-3 py-1.5"
            >
              <Eye size={14} /> Preview
              <ExternalLink size={12} className="opacity-60" />
            </Link>
            <button
              data-testid="reviewer-approve"
              onClick={() => onApprove(row)}
              className="text-sm text-white bg-[color:var(--vv-gold-deep)] hover:opacity-90 rounded-full px-4 py-1.5"
            >
              {isMedicalEditorial(row.page_type) ? "Publish (medical review)" : "Approve & Publish"}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5 text-sm">
          <Stat label="Status" value={status.replace("_", " ")} />
          <Stat label="Words" value={wc.toLocaleString()} />
          <Stat label="Read time" value={`${row.reading_time_min || 0} min`} />
          <Stat label="Verified refs" value={(row.references || []).length} />
          <Stat label="Reviewer flags" value={(row.reviewer_flags || []).length} tone={row.reviewer_flags?.length ? "warn" : ""} />
          <Stat label="Evidence rating" value={row.evidence_rating_status?.replace(/_/g, " ") || "unrated"} />
          <Stat label="AI model" value={row.ai_model || "human"} />
          <Stat label="Featured image" value={row.featured_image ? "present" : "REQUIRED"} tone={row.featured_image ? "" : "warn"} />
        </div>

        <div className="mt-4 text-xs text-stone-600 border-t border-stone-100 pt-3">
          <div><strong>Assigned reviewer:</strong> {row.assigned_medical_reviewer_name || "—"}</div>
          <div><strong>Credited reviewer:</strong> {row.medical_reviewer_name || <em className="text-stone-400">(none — populate on approval)</em>}</div>
          {row.extra?.regulatory_notes ? (
            <div className="mt-2"><strong>Regulatory notes:</strong> {row.extra.regulatory_notes}</div>
          ) : null}
        </div>
      </div>

      {/* Reviewer flags */}
      {row.reviewer_flags?.length ? (
        <div className="vv-card p-5" data-testid="reviewer-flags-panel">
          <div className="text-xs uppercase tracking-widest text-amber-800 mb-2 flex items-center gap-1.5">
            <AlertCircle size={14} /> Reviewer flags — verify before approving
          </div>
          <ol className="space-y-3 text-sm">
            {row.reviewer_flags.map((f, i) => (
              <li key={`flag-${i}`} className="border-l-2 border-amber-200 pl-3">
                <div className="text-stone-800">{f.claim}</div>
                {f.note ? <div className="text-xs text-stone-500 mt-0.5">{f.note}</div> : null}
              </li>
            ))}
          </ol>
        </div>
      ) : null}

      {/* Verified references */}
      {row.references?.length ? (
        <div className="vv-card p-5">
          <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">
            Verified citations ({row.references.length})
          </div>
          <ol className="space-y-2 text-sm list-decimal pl-5">
            {row.references.map((r, i) => (
              <li key={`ref-${i}`}>
                <span className="text-stone-800">{r.title}</span>
                {r.authors?.length ? <span className="text-stone-500"> — {r.authors.slice(0, 3).join(", ")}</span> : null}
                {r.journal ? <span className="text-stone-500 italic"> ({r.journal}{r.year ? `, ${r.year}` : ""})</span> : null}
                <div className="text-[11px] mt-0.5 flex items-center gap-2">
                  <span className={`px-1.5 py-0.5 rounded-full border ${r.verification_status === "verified_pubmed" ? "bg-green-50 text-green-800 border-green-200" : "bg-blue-50 text-blue-800 border-blue-200"}`}>
                    {r.verification_status?.replace(/_/g, " ")}
                  </span>
                  {r.url ? <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-[color:var(--vv-gold-deep)] hover:underline">Open source ↗</a> : null}
                </div>
              </li>
            ))}
          </ol>
        </div>
      ) : null}
    </div>
  );
}

function Stat({ label, value, tone = "" }) {
  const cls = tone === "warn" ? "text-amber-800" : "text-stone-800";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
      <div className={`font-medium ${cls}`}>{value}</div>
    </div>
  );
}

/**
 * Phase 4 — Publication confirmation modal.
 * Only invoked for medical editorial page types (article, condition,
 * symptom, comparison). Publisher (Justin) must:
 *   1. See the validator result — no blockers may exist
 *   2. Tick the required confirmation checkbox
 * before the "Confirm & Publish" button becomes enabled. On confirm the
 * component posts to /admin/content/{type}/{slug}/publish-medical which
 * atomically:
 *   · sets status=published, public_visibility=true
 *   · auto-attributes Dr. Yessenia Quirola as medical_reviewer_name
 *   · auto-attributes Justin Pulliam as publisher_representative_name
 *   · appends an immutable audit event
 */
function MedicalPublishModal({ row, report, onCancel, onConfirm }) {
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const blockers = report?.blockers || [];
  const warnings = report?.warnings || [];
  const validatorOk = report?.ok !== false && blockers.length === 0;
  const canPublish = checked && validatorOk && !submitting;

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm(checked);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      data-testid="medical-publish-modal"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 border-b border-stone-100">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[color:var(--vv-gold-deep)]">
              <ShieldCheck size={14} /> Medical publication confirmation
            </div>
            <h3 className="font-display text-xl mt-1" data-testid="modal-title">{row.title}</h3>
            <div className="text-xs text-stone-500 mt-0.5">{row.page_type} · {row.slug}</div>
          </div>
          <button
            aria-label="Close"
            onClick={onCancel}
            className="text-stone-400 hover:text-stone-800"
            data-testid="modal-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4 text-sm">
          {/* Validator results */}
          <div>
            <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-2">
              Pre-publish validator
            </div>
            {blockers.length ? (
              <div
                className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-900"
                data-testid="validator-blockers"
              >
                <div className="font-medium mb-1 flex items-center gap-1.5">
                  <AlertCircle size={14} /> Cannot publish — {blockers.length} blocker(s):
                </div>
                <ul className="list-disc pl-5 space-y-0.5 text-red-900">
                  {blockers.map((b, i) => <li key={`blocker-${b.slice(0,60)}-${i}`}>{b}</li>)}
                </ul>
              </div>
            ) : (
              <div
                className="rounded-lg border border-green-200 bg-green-50 p-3 text-green-900 flex items-center gap-2"
                data-testid="validator-passed"
              >
                <CheckCircle2 size={16} /> All required checks pass.
              </div>
            )}
            {warnings.length ? (
              <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-900">
                <div className="font-medium mb-1">Warnings (non-blocking):</div>
                <ul className="list-disc pl-5 space-y-0.5">
                  {warnings.map((w, i) => <li key={`warn-${w.slice(0,60)}-${i}`}>{w}</li>)}
                </ul>
              </div>
            ) : null}
          </div>

          {/* Confirmation checkbox */}
          <label
            className="flex items-start gap-3 p-3 rounded-lg border border-stone-200 bg-stone-50 cursor-pointer hover:border-stone-400"
            data-testid="confirm-checkbox-label"
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-1"
              data-testid="confirm-checkbox"
            />
            <span className="text-stone-900">
              <strong className="block">
                I confirm that Dr. Yessenia Quirola has reviewed and approved this
                medical content for publication.
              </strong>
              <span className="block text-xs text-stone-500 mt-1">
                On confirm, the system will credit Dr. Quirola as medical reviewer,
                credit you as publisher, timestamp the review, and write an immutable
                audit event tied to this page revision.
              </span>
            </span>
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-stone-100 bg-stone-50">
          <button
            onClick={onCancel}
            className="text-sm px-4 py-2 rounded-full border border-stone-300 hover:bg-white"
            data-testid="modal-cancel"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canPublish}
            className={`text-sm px-4 py-2 rounded-full text-white transition-colors ${
              canPublish
                ? "bg-[color:var(--vv-gold-deep)] hover:opacity-90"
                : "bg-stone-300 cursor-not-allowed"
            }`}
            data-testid="modal-confirm-publish"
          >
            {submitting ? "Publishing…" : "Confirm & Publish"}
          </button>
        </div>
      </div>
    </div>
  );
}
